import { AlertTriangle, ArrowDownToLine, BookmarkCheck, CheckCircle2, Loader2, Receipt, Save, ShieldAlert } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { api } from '@/api/client';
import { fetchMe, setWallets, withdraw } from '@/api/member';
import { fmtUsdAmount as fmtUsd } from '@/lib/money';

/**
 * LiveWithdrawPanel — user-facing withdrawal request flow.
 *
 * Behavior:
 *   1) User picks asset (LTC / TRX / DOGE) and enters a USD amount (\u2265 $10).
 *   2) The panel pre-fills the destination address from the user's saved
 *      Profile wallet for that asset. If none saved, the user is required
 *      to paste one inline (the backend also accepts this fallback path).
 *   3) If the user types a NEW address (different from the saved one), a
 *      "Save this address to my profile for future withdrawals" checkbox
 *      appears (default: ON). On submit, the address is persisted to
 *      users.wallet_addresses[asset] BEFORE the withdrawal POST so the
 *      next visit pre-fills it. A standalone "Save address" button lets
 *      the user persist a new address without triggering a withdrawal.
 *   4) Click "Request Withdrawal" \u2192 POST /api/me/withdraw. Backend
 *      atomically debits users.withdrawal_balance and writes a Withdrawal
 *      doc in status="pending".
 *   5) The history list below polls /api/me/ledger?type=Withdrawal so the
 *      user immediately sees the new request.
 *
 * IMPORTANT: this is REQUEST-time UX. The actual payout happens later when
 * an admin compiles the Mass Payout batch. We make this explicit in the
 * help copy so users aren't confused that funds don't arrive instantly.
 */
const SUPPORTED_ASSETS = [
  { code: 'LTC', label: 'Litecoin' },
  { code: 'TRX', label: 'TRON' },
  { code: 'DOGE', label: 'Dogecoin' },
];
const MIN_USD = 10;

const LiveWithdrawPanel = ({ me: meProp, onRefresh }) => {
  const [me, setMe] = useState(meProp || null);
  const [asset, setAsset] = useState('LTC');
  const [amountStr, setAmountStr] = useState('');
  const [destOverride, setDestOverride] = useState('');
  const [saveToProfile, setSaveToProfile] = useState(true);
  const [savingAddress, setSavingAddress] = useState(false);
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState({ items: [], total: 0 });
  const [historyLoading, setHistoryLoading] = useState(true);

  const refreshMe = useCallback(async () => {
    try {
      const r = await fetchMe();
      setMe(r);
    } catch {
      /* non-critical */
    }
  }, []);

  const refreshHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const r = await api.get('/api/me/ledger', { params: { type: 'Withdrawal', page: 1 } }).then((x) => x.data);
      setHistory({ items: r.items || [], total: r.total || 0 });
    } catch {
      setHistory({ items: [], total: 0 });
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!meProp) refreshMe();
  }, [meProp, refreshMe]);

  useEffect(() => {
    refreshHistory();
  }, [refreshHistory]);

  // When the user switches assets, clear the override + reset the
  // save-to-profile checkbox so it never carries stale intent across
  // different chains.
  // biome-ignore lint/correctness/useExhaustiveDependencies: `asset` is the intentional trigger; reset the override whenever the selected chain changes
  useEffect(() => {
    setDestOverride('');
    setSaveToProfile(true);
  }, [asset]);

  const savedAddress = me?.wallet_addresses?.[asset] || '';
  const effectiveAddress = (destOverride.trim() || savedAddress).trim();
  const hasAddress = Boolean(effectiveAddress);
  // "New address" = the user typed something AND it differs from what's
  // already saved on their profile for the selected asset. Only then does
  // the save-to-profile UI make sense.
  const typedAddress = destOverride.trim();
  const isNewAddress = typedAddress.length > 0 && typedAddress !== savedAddress;

  const balance = Number(me?.withdrawal_balance || 0);
  const amountNum = Number(amountStr || 0);
  const overBalance = amountNum > balance + 0.0001;
  const belowMin = amountNum > 0 && amountNum < MIN_USD;

  const canSubmit = !busy && amountNum >= MIN_USD && !overBalance && hasAddress && Number.isFinite(amountNum);

  /**
   * Persists the typed address to the user's profile via PUT /api/me/wallets
   * for the currently-selected asset. Returns true on success, false on
   * failure (with a toast). Safe to call inside the submit flow OR via the
   * standalone "Save address" button.
   */
  const persistAddress = async () => {
    if (!isNewAddress) return true; // nothing to do
    setSavingAddress(true);
    try {
      const updated = await setWallets({ [asset]: typedAddress });
      setMe(updated);
      return true;
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === 'object' ? detail?.message || detail?.code : detail || err.message;
      toast.error(`Couldn't save address: ${msg}`);
      return false;
    } finally {
      setSavingAddress(false);
    }
  };

  /**
   * Standalone "Save address" — persists the typed address WITHOUT
   * triggering a withdrawal. Useful when the user just wants to update
   * their saved destination from this page instead of jumping to Profile.
   */
  const handleSaveAddressOnly = async () => {
    if (!isNewAddress || savingAddress) return;
    const ok = await persistAddress();
    if (ok) {
      toast.success(`${asset} address saved to your profile for future withdrawals.`);
      setDestOverride(''); // collapse the override input — it's now saved
      await onRefresh?.();
    }
  };

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    try {
      // If the user typed a NEW address and opted to save it (checkbox is
      // ON), persist BEFORE the withdrawal POST. This way the saved
      // address shows up immediately when the page refreshes after a
      // successful withdrawal, and the user has a clean record going
      // forward. A save failure is non-fatal — we still attempt the
      // withdrawal with the typed address so the user isn't blocked.
      if (isNewAddress && saveToProfile) {
        await persistAddress();
      }
      const _r = await withdraw(String(amountNum.toFixed(2)), asset, effectiveAddress);
      toast.success(`Withdrawal request received — ${asset} $${fmtUsd(amountNum)}. You'll receive an email when it's officially paid out.`);
      setAmountStr('');
      setDestOverride('');
      await Promise.all([refreshMe(), refreshHistory()]);
      await onRefresh?.();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === 'object' ? detail?.message || detail?.code : detail || err.message;
      toast.error(`Withdrawal failed: ${msg}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="live-withdraw-root">
      {/* HERO: balance + form */}
      <section
        className="sc-glass-strong rounded-2xl p-5 md:p-7 relative overflow-hidden"
        style={{ border: '1px solid rgba(255,182,18,0.32)' }}
        data-testid="live-withdraw-panel"
      >
        <div
          className="absolute -top-20 -right-20 w-80 h-80 rounded-full pointer-events-none"
          style={{
            background: 'radial-gradient(circle, rgba(255,182,18,0.18), transparent 60%)',
          }}
          aria-hidden="true"
        />
        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-4">
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center border shrink-0"
              style={{
                background: 'rgba(255,182,18,0.08)',
                borderColor: 'rgba(255,182,18,0.32)',
              }}
              aria-hidden="true"
            >
              <ArrowDownToLine className="w-5 h-5 text-[#FFC20E]" />
            </div>
            <div className="min-w-0">
              <div className="text-[11px] uppercase tracking-[0.22em] text-[var(--sc-text-mute)]">Withdraw · Live</div>
              <div className="font-display font-bold text-base md:text-lg mt-0.5">Request a payout from your Withdrawal Balance</div>
            </div>
          </div>

          {/* Balance hero */}
          <div
            className="rounded-xl border p-4 mb-5"
            style={{
              background: 'rgba(0,0,0,0.32)',
              borderColor: 'rgba(255,182,18,0.24)',
            }}
            data-testid="withdraw-balance-card"
          >
            <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--sc-text-mute)]">Available to withdraw</div>
            <div className="sc-tabular font-display font-bold text-3xl md:text-4xl mt-1 sc-gold-text" data-testid="withdraw-balance-value">
              ${fmtUsd(balance)}
            </div>
            <div className="text-[11px] text-[var(--sc-text-dim)] mt-1">
              Minimum withdrawal ${fmtUsd(MIN_USD)}. Cycles, matching bonuses, and adoption rewards build this balance.
            </div>
          </div>

          {/* Asset picker */}
          <div className="mb-4">
            <div className="text-[10px] uppercase tracking-[0.20em] text-[var(--sc-text-mute)] mb-2">Asset</div>
            <div className="flex items-center gap-2 flex-wrap">
              {SUPPORTED_ASSETS.map((a) => {
                const selected = asset === a.code;
                return (
                  <button
                    key={a.code}
                    type="button"
                    onClick={() => setAsset(a.code)}
                    className="px-3 py-1.5 rounded-full text-[11px] uppercase tracking-[0.16em] border transition"
                    style={{
                      color: selected ? '#0a0a0b' : 'var(--sc-text-mute)',
                      background: selected ? 'linear-gradient(135deg, #FFC20E, #FFB612)' : 'rgba(0,0,0,0.32)',
                      borderColor: selected ? 'rgba(255,182,18,0.85)' : 'rgba(255,182,18,0.18)',
                      fontWeight: selected ? 700 : 500,
                    }}
                    data-testid={`withdraw-asset-${a.code}`}
                  >
                    {a.code} · {a.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Amount + destination */}
          <div className="grid grid-cols-1 md:grid-cols-[1fr_2fr] gap-3 md:gap-4 mb-4">
            <div>
              <div className="text-[10px] uppercase tracking-[0.20em] text-[var(--sc-text-mute)] mb-2">Amount (USD)</div>
              <div className="rounded-xl border bg-[rgba(0,0,0,0.32)] border-[rgba(255,182,18,0.32)] flex items-center px-3 h-[42px]">
                <span className="text-[var(--sc-text-mute)] mr-2">$</span>
                <input
                  type="number"
                  inputMode="decimal"
                  min={MIN_USD}
                  step="0.01"
                  value={amountStr}
                  onChange={(e) => setAmountStr(e.target.value)}
                  className="flex-1 bg-transparent outline-none text-base font-display sc-tabular"
                  placeholder={String(MIN_USD)}
                  data-testid="withdraw-amount-input"
                />
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.20em] text-[var(--sc-text-mute)] mb-2">
                {asset} destination address
                {savedAddress ? (
                  <span className="ml-2 normal-case tracking-normal text-[10px] text-[#10B981]">✓ Using saved profile address</span>
                ) : (
                  <span className="ml-2 normal-case tracking-normal text-[10px] text-[#EF4444]">no saved address — paste one below</span>
                )}
              </div>
              <input
                type="text"
                value={destOverride}
                onChange={(e) => setDestOverride(e.target.value)}
                className="w-full rounded-xl border bg-[rgba(0,0,0,0.32)] border-[rgba(255,182,18,0.32)] px-3 h-[42px] outline-none font-mono text-[12px]"
                placeholder={savedAddress ? `${savedAddress.slice(0, 12)}… (leave blank to use saved)` : `Paste your ${asset} address`}
                data-testid="withdraw-address-input"
                autoComplete="off"
                spellCheck="false"
              />
              {/* Save-to-profile row — only renders when the user has
                  typed an address that DIFFERS from their saved one. */}
              {isNewAddress && (
                <div
                  className="mt-2 flex flex-wrap items-center justify-between gap-2 rounded-xl border px-3 py-2"
                  style={{
                    background: 'rgba(255,182,18,0.06)',
                    borderColor: 'rgba(255,182,18,0.28)',
                  }}
                  data-testid="withdraw-save-address-row"
                >
                  <label className="flex items-center gap-2 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={saveToProfile}
                      onChange={(e) => setSaveToProfile(e.target.checked)}
                      className="w-4 h-4 accent-[#FFC20E] cursor-pointer"
                      data-testid="withdraw-save-address-checkbox"
                    />
                    <span className="text-[12px] text-[var(--sc-text)] leading-snug">
                      <BookmarkCheck className="w-3.5 h-3.5 inline-block mr-1 text-[#FFC20E] -mt-0.5" />
                      Save this {asset} address to my profile for future withdrawals
                      {savedAddress ? (
                        <span className="block text-[10px] text-[var(--sc-text-mute)] mt-0.5">Replaces saved: {savedAddress.slice(0, 14)}…</span>
                      ) : (
                        <span className="block text-[10px] text-[var(--sc-text-mute)] mt-0.5">No saved address yet for {asset} — it'll be added.</span>
                      )}
                    </span>
                  </label>
                  <button
                    type="button"
                    onClick={handleSaveAddressOnly}
                    disabled={savingAddress}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] uppercase tracking-[0.16em] font-bold transition disabled:opacity-60 shrink-0"
                    style={{
                      background: 'rgba(0,0,0,0.42)',
                      border: '1px solid rgba(255,182,18,0.55)',
                      color: '#FFE08A',
                    }}
                    data-testid="withdraw-save-address-btn"
                    title="Save this address without submitting a withdrawal"
                  >
                    {savingAddress ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                    Save address
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Validation hints */}
          <div className="space-y-2 mb-4">
            {belowMin && (
              <div className="flex items-center gap-2 text-[12px] text-[#EF4444]" data-testid="withdraw-below-min">
                <AlertTriangle className="w-3.5 h-3.5" />
                Minimum withdrawal is ${fmtUsd(MIN_USD)}.
              </div>
            )}
            {overBalance && (
              <div className="flex items-center gap-2 text-[12px] text-[#EF4444]" data-testid="withdraw-over-balance">
                <AlertTriangle className="w-3.5 h-3.5" />
                Amount exceeds your withdrawal balance of ${fmtUsd(balance)}.
              </div>
            )}
            {!hasAddress && (
              <div className="flex items-center gap-2 text-[12px] text-[#FFC20E]" data-testid="withdraw-no-address">
                <ShieldAlert className="w-3.5 h-3.5" />
                Save a {asset} address in your Profile, or paste one above.
              </div>
            )}
          </div>

          <button
            type="button"
            onClick={submit}
            disabled={!canSubmit}
            className="w-full md:w-auto inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl font-bold text-[13px] uppercase tracking-[0.18em] text-black disabled:opacity-60 transition"
            style={{
              background: 'linear-gradient(135deg, #FFC20E, #FFB612)',
              border: '1px solid rgba(255,182,18,0.85)',
            }}
            data-testid="withdraw-submit"
          >
            {busy ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Submitting…
              </>
            ) : (
              <>
                <ArrowDownToLine className="w-4 h-4" />
                Request withdrawal
              </>
            )}
          </button>

          <div className="mt-3 text-[11px] text-[var(--sc-text-dim)] leading-relaxed">
            Requests enter the payout queue immediately. Your funds dispatch on the next batch the admin compiles. You'll receive an email confirmation the
            moment NOWPayments dispatches the on-chain transfer.
          </div>
        </div>
      </section>

      {/* HISTORY */}
      <section className="sc-glass-strong rounded-2xl p-5 md:p-6" data-testid="withdraw-history-card">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <div className="flex items-center gap-2">
            <Receipt className="w-4 h-4 text-[#FFC20E]" />
            <div className="font-display font-bold text-base md:text-lg">Recent withdrawal requests</div>
          </div>
          <div className="text-[11px] uppercase tracking-[0.22em] text-[var(--sc-text-mute)]">{history.total} total</div>
        </div>
        {historyLoading ? (
          <div className="p-6 flex items-center justify-center text-[var(--sc-text-dim)] text-sm" data-testid="withdraw-history-loading">
            <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading…
          </div>
        ) : !history.items.length ? (
          <div className="p-8 text-center text-[var(--sc-text-dim)] text-sm" data-testid="withdraw-history-empty">
            No withdrawal requests yet. Submit one above to get started.
          </div>
        ) : (
          <div className="overflow-x-auto -mx-2 md:mx-0">
            <table className="w-full text-[13px]">
              <thead>
                <tr className="text-[10px] uppercase tracking-[0.20em] text-[var(--sc-text-mute)] border-b border-[rgba(255,182,18,0.10)]">
                  <th className="text-left font-medium py-2 px-3">Date</th>
                  <th className="text-right font-medium py-2 px-3">Amount</th>
                  <th className="text-left font-medium py-2 px-3 hidden md:table-cell">Asset</th>
                  <th className="text-left font-medium py-2 px-3 hidden md:table-cell">Memo</th>
                </tr>
              </thead>
              <tbody>
                {history.items.slice(0, 10).map((row) => {
                  const asset = row.metadata?.asset || row.metadata?.currency || '';
                  const amt = Math.abs(Number(row.amount || 0));
                  return (
                    <tr
                      key={row.id || `${row.timestamp}-${row.amount}`}
                      className="border-b border-[rgba(255,182,18,0.05)]"
                      data-testid={`withdraw-history-row-${row.id || row.timestamp}`}
                    >
                      <td className="py-2 px-3 text-[12px] text-[var(--sc-text-dim)]">
                        {String(row.timestamp || '')
                          .slice(0, 16)
                          .replace('T', ' ')}
                      </td>
                      <td className="py-2 px-3 text-right sc-tabular font-semibold sc-gold-text">−${fmtUsd(amt)}</td>
                      <td className="py-2 px-3 hidden md:table-cell text-[11px] uppercase tracking-[0.18em] text-[var(--sc-text-mute)]">{asset}</td>
                      <td className="py-2 px-3 hidden md:table-cell text-[11px] text-[var(--sc-text-mute)]">{row.memo || ''}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Success hint */}
      <div className="text-[11px] text-[var(--sc-text-mute)] flex items-center gap-1.5">
        <CheckCircle2 className="w-3 h-3 text-[#10B981]" />
        Withdrawal requests are atomic — funds are reserved from your balance the moment you submit, preventing double-spend.
      </div>
    </div>
  );
};

export default LiveWithdrawPanel;
